/**
 * grammar-spec.ts — a static, LLM-facing reference for the proof. annotation
 * grammar (Track C, C-WP3 — the `get_grammar_spec` tool's payload).
 *
 * PURPOSE. This module exports a single markdown string, {@link GRAMMAR_SPEC_MARKDOWN},
 * that grounds an assistant model in the grammar's constructs, units, and
 * directives. It is the machine-readable sibling of the human help panel
 * (`apps/web/src/features/help/SyntaxHelp.tsx`) — it COVERS what that panel
 * teaches, but is SOURCED FROM the grammar package's own tables, never
 * copy-pasted from the UI strings. A grammar change (a new unit, a new
 * construct directive, a new section trait) updates the spec automatically at
 * module load, so the two cannot drift.
 *
 * SOURCE OF TRUTH (build plan C-WP3, line 150). The catalogues are BUILT from
 * the imported grammar tables:
 *   - {@link GRAMMAR_CONSTRUCT_DIRECTIVES} (lexer)  → @bind / @define / @define-section
 *   - {@link RESERVED_DIRECTIVES} (lexer)           → @end / @scale (the TWO reserved
 *                                                     keywords — never the directive catalogue)
 *   - {@link UNITS} (units/dimensions)              → the unit + dimension table
 *   - {@link SECTION_DEFAULT_TRAITS} + {@link TRAITS} → the seed-section trait table
 *   - {@link SCALABLE_SECTIONS} (evaluator/graph)   → the @scale scalable set
 *   - {@link GEOMETRY_FN_NAMES} (stdlib/geometry)   → the geometry stdlib
 * Only the PROSE GLUE (expression syntax, @rating / @intent semantics, density
 * hints, aggregation names, "where values appear") is hand-written — the tables
 * themselves are always derived.
 *
 * SANDBOX INVARIANT. This module is PURE data assembly. It must NOT be reachable
 * from the evaluator Worker bundle (`../evaluator/worker-entry.ts`) — it is
 * exported only from the grammar package root and consumed server-side by the
 * `get_grammar_spec` tool. It imports the same read-only tables the root already
 * carries, so it adds no new heavy dependency.
 *
 * Spec refs: extensibility-design.md §12 (MVP grammar features), §3 / §4 / §5;
 *   .plan/implementation/2026-07-10-track-c-ai-mcp-build-plan.md C-WP3.
 */

import { GRAMMAR_CONSTRUCT_DIRECTIVES, RESERVED_DIRECTIVES } from "../lexer/index.js";
import { UNITS, type Dimension } from "../units/dimensions.js";
import { TRAITS, SECTION_DEFAULT_TRAITS } from "../traits/index.js";
import { SCALABLE_SECTIONS } from "../evaluator/graph.js";
import { GEOMETRY_FN_NAMES } from "../stdlib/geometry.js";

// ── Human labels for the derived tables (fall back to the raw key so a new
//    table entry never silently drops out of the spec) ─────────────────────────

/** A readable heading per unit dimension; unknown dimensions fall back to the key. */
const DIMENSION_LABEL: Readonly<Record<Dimension, string>> = {
  mass: "Mass",
  volume: "Volume (liquid + geometric — 1 ml = 1 cm³)",
  length: "Length",
  area: "Area (derived — length × length)",
  temperature: "Temperature (non-linear conversion)",
  time: "Time",
  ratio: "Ratio / percent",
  count: "Count (dimensionless)",
  unknown: "Unknown",
};

/** One-line gloss per grammar-construct directive keyword. */
const CONSTRUCT_DIRECTIVE_GLOSS: Readonly<Record<string, string>> = {
  bind: "declares a named virtual variable from an expression with a single `pivot:` for back-solve",
  define: "declares a reusable named formula callable from any expression",
  "define-section": "declares a custom section type with a trait set",
};

/** One-line gloss per reserved directive keyword (the TWO reserved keywords only). */
const RESERVED_DIRECTIVE_GLOSS: Readonly<Record<string, string>> = {
  end: "closes the enclosing section block explicitly",
  scale: "multiplies every quantity in a scalable section by a factor (`@scale x1.5`)",
};

/** Signature gloss per geometry stdlib function; unknown names fall back to `fn(...)`. */
const GEOMETRY_SIGNATURE: Readonly<Record<string, string>> = {
  cylinder: "cylinder(radius, height) → volume (ml = cm³)",
  rect: "rect(length, width, height) → volume (ml = cm³)",
  ring: "ring(outer_radius, inner_radius, height) → volume (ml = cm³)",
  sphere: "sphere(radius) → volume (ml = cm³)",
};

// ── Table builders (each returns a markdown fragment derived from a table) ──────

/** Group the shipped units by dimension, preserving the table's declaration order. */
function unitsByDimension(): Map<Dimension, string[]> {
  const grouped = new Map<Dimension, string[]>();
  for (const u of UNITS) {
    // The empty-symbol "count" unit has no printable symbol — represent it as a
    // parenthetical so the `count` dimension still surfaces in the table.
    const symbol = u.symbol === "" ? "(dimensionless)" : u.symbol;
    const list = grouped.get(u.dimension);
    if (list) {
      if (!list.includes(symbol)) list.push(symbol);
    } else {
      grouped.set(u.dimension, [symbol]);
    }
  }
  return grouped;
}

function buildUnitsSection(): string {
  const lines = ["## Units and dimensions", ""];
  lines.push("Units attach to literal values and propagate through arithmetic. A");
  lines.push("dimension mismatch (e.g. adding grams to millilitres without a density) is a");
  lines.push("type error, surfaced on the line and in the Values panel.");
  lines.push("");
  for (const [dimension, symbols] of unitsByDimension()) {
    const label = DIMENSION_LABEL[dimension] ?? dimension;
    lines.push(`- **${label}** — ${symbols.join(", ")}`);
  }
  lines.push("");
  lines.push("Temperature conversion (°C ↔ °F ↔ K) is non-linear and handled internally.");
  return lines.join("\n");
}

function buildSectionTraitsSection(): string {
  const traitGloss = new Map(TRAITS.map((t) => [t.name, t.description]));
  const lines = ["## Section keywords and traits", ""];
  lines.push("A `@keyword` line opens a section block; labelled lines inside it become");
  lines.push("variables. Section blocks close at the next heading, the next `@keyword`,");
  lines.push("`@end`, or end of document. Unknown keywords are NOT errors — they parse as");
  lines.push("custom sections and their labelled lines still evaluate.");
  lines.push("");
  lines.push("Seed keywords carry built-in traits:");
  lines.push("");
  for (const [keyword, traits] of SECTION_DEFAULT_TRAITS) {
    lines.push(`- **@${keyword}** — ${traits.join(", ")}`);
  }
  lines.push("");
  lines.push("Trait meanings:");
  lines.push("");
  for (const trait of TRAITS) {
    lines.push(`- **${trait.name}** — ${traitGloss.get(trait.name) ?? ""}`);
  }
  return lines.join("\n");
}

function buildDirectivesSection(): string {
  const lines = ["## Grammar-construct directives", ""];
  lines.push("These single-line `@keyword` directives declare constructs — they never open");
  lines.push("a section block:");
  lines.push("");
  for (const keyword of GRAMMAR_CONSTRUCT_DIRECTIVES.keys()) {
    lines.push(`- **@${keyword}** — ${CONSTRUCT_DIRECTIVE_GLOSS[keyword] ?? "grammar construct"}`);
  }
  lines.push("");
  lines.push("Reserved directive keywords (imperative, single-line — never a section header):");
  lines.push("");
  for (const keyword of RESERVED_DIRECTIVES) {
    lines.push(`- **@${keyword}** — ${RESERVED_DIRECTIVE_GLOSS[keyword] ?? "reserved directive"}`);
  }
  return lines.join("\n");
}

function buildScaleSection(): string {
  const scalable = [...SCALABLE_SECTIONS].map((s) => `@${s}`).join(", ");
  const lines = ["## @scale — quantity scaling", ""];
  lines.push("`@scale x1.5` (or `1.5x`) multiplies every ingredient quantity in the scope it");
  lines.push("sits in by the factor. Each row keeps its base value AND its scaled value; both");
  lines.push("render in Cook and Read view and the Values panel.");
  lines.push("");
  lines.push(`Only sections in the scalable set scale: ${scalable}.`);
  lines.push("`@oven` and `@ferment` are intentionally NOT scaled — temperatures and");
  lines.push("durations do not scale proportionally with batch size.");
  return lines.join("\n");
}

function buildGeometrySection(): string {
  const lines = ["## Geometry stdlib", ""];
  lines.push("Callable from any expression; all operands must be length-dimension values,");
  lines.push("results are volumes in ml (= cm³):");
  lines.push("");
  for (const name of GEOMETRY_FN_NAMES) {
    lines.push(`- \`${GEOMETRY_SIGNATURE[name] ?? `${name}(...)`}\``);
  }
  return lines.join("\n");
}

// ── Hand-written prose glue (constructs the tables cannot express) ─────────────

const OVERVIEW = `# proof. grammar reference

The annotation grammar turns recipe text into an evaluated document. Labels
become variables; expressions reference those variables; sections carry semantic
traits. Results appear in the editor (as \`→ value\` chips), in Cook view, and in
the Values panel. The grammar is a CLOSED SANDBOX: text is parsed, never
evaluated as code — there is no \`eval\`, no function-body injection.

This reference is generated from the grammar's own tables. It is for grounding
an assistant that reads or writes recipe bodies.`;

const STRUCTURE = `## Structure and headings

Markdown headings (\`#\` through \`######\`) open named scopes — a scope groups
variables and nests by heading level. Comments: \`// line\`, \`/* block */\`,
\`<!-- html -->\`.

    ## Dough
    @ingredients
    flour: 500g

    ## Levain
    @ingredients
    flour: 100g`;

const LABELLED_LINES = `## Labelled lines and variables

A \`label: value\` line INSIDE a section block creates a variable — the label is
the key (lowercased internally), the right-hand side is the value or expression.
Labels in plain prose or headings do NOT create variables.

    @ingredients
    flour: 500g
    water: 360g`;

const EXPRESSIONS = `## Expressions

Prefix a value with \`=\` to write an expression referencing other variables —
this is the canonical form and always works.

    @ingredients
    flour: 500g
    water: 360g
    hydration: =water / flour * 100

Operators: \`+ - * / ^\`, unary minus, parentheses. References resolve in the same
section first, then across the document. Scope-qualified: \`Levain/flour\`.
Parent-scope: \`../flour\`. Subtraction needs spaces around the minus
(\`=total - used\`) — hyphens are legal inside variable names (\`bread-flour\`).`;

const RATING = `## @rating

An \`@rating\` section treats labelled lines as ratings. A bare numerator uses 5 as
the default denominator; \`yes\` / \`no\` infer a boolean. An inline form places all
ratings on one line.

    @rating
    crust: 4/5
    crumb: 5
    sour: yes

    @rating 4/5 (taste)

The evaluated value of \`4/5\` is 0.8 (arithmetic division); the numerator /
denominator metadata drives the star display.

User-declared rating dimension types (in \`.cookrc.yml\`, referenced by name):
stars, boolean, enum, number, text, coord, duration, photo_ref, artefact_ref.`;

const INTENT = `## @intent

\`@intent less yeast, longer cold ferment\` records this version's one-line
hypothesis. It is an inert annotation — never evaluated, never in the Values
panel — frozen with the version and stripped from the successor draft.`;

const DEFINE_BIND = `## @define and @bind

\`@define f(a, b) = a / b * 100\` declares a named formula, collected before
evaluation and callable from any expression.

\`@bind hydration = water / flour * 100, pivot: water\` declares a named virtual
variable that evaluates forward, plus the pivot to rewrite on back-solve. At MVP
only SINGLE-PIVOT linear / multiplicative expressions are invertible; anything
else evaluates forward and shows a read-only chip.`;

const DENSITY = `## Density conversions

Mass-to-volume conversion uses density. Add an inline hint after the unit —
\`olive oil: 100ml (@0.92 g/ml)\` — or rely on the shipped reference-density
registry (default-on, signed bundle). Per-library overrides live in \`.cookrc.yml\`.`;

const AGGREGATIONS = `## Aggregations

\`sum\`, \`count\`, \`max\`, \`min\`, \`avg\`, \`first\`, \`last\` aggregate over a set of
values, with an optional bounded \`where\` filter. Aggregations evaluate forward
only — they are not invertible.`;

const WHERE_VALUES = `## Where computed values appear

- **Editor** — each labelled equation line shows a \`→ value\` chip; where the
  expression is invertible the chip is a control that back-solves the pivot.
- **Cook view** — the read view shows each labelled line's evaluated value.
- **Values panel** — lists every resolved variable in scope.`;

const NOT_YET = `## Not yet supported (Phase 2)

- Multi-pivot back-solve (simultaneous constraints). MVP is single-pivot only.
- Exponent / function / aggregation inversion — \`^\`, \`sum()\`, \`avg()\` evaluate
  forward and show a read-only chip.`;

// ── Assembly ───────────────────────────────────────────────────────────────────

/**
 * Assemble the full grammar reference markdown from the imported tables + prose
 * glue. Called once at module load ({@link GRAMMAR_SPEC_MARKDOWN}); exported so a
 * test (or a future consumer) can re-derive it and assert table-coverage.
 */
export function buildGrammarSpecMarkdown(): string {
  return (
    [
      OVERVIEW,
      STRUCTURE,
      LABELLED_LINES,
      EXPRESSIONS,
      buildSectionTraitsSection(),
      buildDirectivesSection(),
      buildScaleSection(),
      RATING,
      INTENT,
      DEFINE_BIND,
      buildUnitsSection(),
      DENSITY,
      buildGeometrySection(),
      AGGREGATIONS,
      WHERE_VALUES,
      NOT_YET,
    ].join("\n\n") + "\n"
  );
}

/**
 * The assembled grammar reference — a stable markdown string for LLM grounding.
 * Built once at module load from the grammar tables, so a grammar change is
 * reflected here automatically.
 */
export const GRAMMAR_SPEC_MARKDOWN: string = buildGrammarSpecMarkdown();
